import { useEffect, useMemo, useRef } from 'react';

import { debounce } from 'lodash';

interface IDebounceSaveProps<T> {
  data: T;
  saveFunction: (data: T) => void;
  debounceTime?: number;
  skipExecutionOnFirstRender?: boolean;
}

export const useDebouncedSave = <T>({
  data,
  saveFunction,
  debounceTime = 1000,
  skipExecutionOnFirstRender = false,
}: IDebounceSaveProps<T>) => {
  const hasBeenFirstRender = useRef(!skipExecutionOnFirstRender);
  const debouncedSave = useMemo(() => debounce(saveFunction, debounceTime), [saveFunction, debounceTime]);

  useEffect(() => {
    if (hasBeenFirstRender.current) {
      debouncedSave(data);
    }
    hasBeenFirstRender.current = true;

    return () => debouncedSave.cancel();
  }, [data, debouncedSave]);
};
